/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import { 
  Check, 
  TextT, 
  ArrowCounterClockwise, 
  TextAa, 
  TextAUnderline, 
  ArrowsOutLineVertical,
  ToggleLeft,
  EyeSlash,
  Link as LinkIcon,
  MagicWand
} from "@phosphor-icons/react";
import { defaultResumeData } from "@stylingcv/schema";
import type { ComboboxOption } from "@stylingcv/ui";
import {
  Button,
  Combobox,
  Label,
  Slider,
  Tooltip,
} from "@stylingcv/ui";
import { cn, fonts } from "@stylingcv/utils";
import { useCallback, useEffect, useState } from "react";
import webfontloader from "webfontloader";

import { useResumeStore } from "@/client/stores/resume";

const localFonts = ["Arial", "Cambria", "Garamond", "Times New Roman"];

const fontSuggestions = [
  ...localFonts,
  "IBM Plex Sans",
  "IBM Plex Serif",
  "Lato",
  "Lora",
  "Merriweather",
  "Open Sans",
  "Playfair Display",
  "PT Sans",
  "PT Serif",
  "Roboto Condensed",
];

const ArabicfontSuggestions = [
  ...localFonts,
  "Noto Kufi Arabic",
  "El Messiri",
  "Reem Kufi",
  "Cairo",
  "Amiri",
  "Changa",
  "Lateef",
  "Lemonada",
  "Aref Ruqaa",
  "Droid Arabic Naskh",
  "Tajawal",
];

const families = fonts.map((font) => ({
  value: font.family,
  label: font.family,
  type: font.subsets.includes("serif") ? "Serif" : "Sans Serif"
})) satisfies (ComboboxOption & { type?: string })[];

families.push(...localFonts.map((font) => ({ value: font, label: font, type: "System" })));

export const TypographySection = () => {
  const [subsets, setSubsets] = useState<ComboboxOption[]>([]);
  const [variants, setVariants] = useState<ComboboxOption[]>([]);

  const setValue = useResumeStore((state) => state.setValue);
  const metadata = useResumeStore((state) => {
    try {
      return state.resume.data.metadata || defaultResumeData.metadata;
    } catch {
      return defaultResumeData.metadata;
    }
  });
  
  const locale =
    typeof metadata === "object" && "locale" in metadata && typeof metadata.locale === "string"
      ? metadata.locale.toLowerCase()
      : "";
  const isArabic = locale === "ar" || locale === "arabic" || locale.startsWith("ar-");
  const fontSuggestionsToUse = isArabic ? ArabicfontSuggestions : fontSuggestions;

  const loadFontSuggestions = useCallback(() => {
    for (const font of fontSuggestionsToUse) {
      if (localFonts.includes(font)) continue;

      webfontloader.load({
        events: false,
        classes: false,
        google: { families: [font], text: font },
      });
    }
  }, [fontSuggestionsToUse]);

  useEffect(() => {
    loadFontSuggestions();
  }, [loadFontSuggestions]);

  useEffect(() => {
    const fontFamily =
      metadata.typography.font.family || defaultResumeData.metadata.typography.font.family;
    const subsets = fonts.find((font) => font.family === fontFamily)?.subsets ?? [];
    if (subsets.length > 0) {
      setSubsets(subsets.map((subset) => ({ value: subset, label: subset })));
    }

    const variants = fonts.find((font) => font.family === fontFamily)?.variants ?? [];
    if (variants.length > 0) {
      setVariants(variants.map((variant) => ({ value: variant, label: variant })));
    }
  }, [metadata.typography.font.family]);

  useEffect(() => {
    if (isArabic) {
      const currentFont = metadata.typography.font.family;
      if (!currentFont || !ArabicfontSuggestions.includes(currentFont)) {
        setValue("metadata.typography.font.family", "Tajawal");
        setValue("metadata.typography.font.subset", "arabic");
        setValue("metadata.typography.font.variants", ["regular"]);
      }
    }
  }, [isArabic, metadata.typography.font.family, setValue]);

  const font = metadata.typography.font || defaultResumeData.metadata.typography.font;
  
  const onResetTypography = () => {
    try {
      setValue("metadata.typography", defaultResumeData.metadata.typography);
    } catch {
      // Silent fail
    }
  };

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-5">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
            <TextT size={20} className="text-gray-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#16344c]">{t`Typography`}</h3>
            <p className="text-[11px] text-gray-400">{t`Fonts, sizes & character`}</p>
          </div>
        </div>
        <Tooltip content={t`Reset Typography`}>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors" onClick={onResetTypography}>
            <ArrowCounterClockwise size={18} />
          </Button>
        </Tooltip>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-6">
      {/* Font Selection */}
      <div className="space-y-6 relative">
        {/* Quick Select Grid */}
        <div className="grid grid-cols-2 gap-3">
          {fontSuggestionsToUse
            .sort((a, b) => a.localeCompare(b))
            .map((fontName) => {
              const isSelected = font.family === fontName;
              return (
                <div key={fontName} className="relative">
                  <button
                    style={{ fontFamily: fontName }}
                    className={cn(
                      "flex h-12 w-full items-center justify-center overflow-hidden rounded-xl border text-center text-xs transition-all duration-200 lg:text-sm relative group",
                      isSelected
                        ? "bg-[#268599]/10 border-[#268599] text-[#268599]"
                        : "bg-white border-gray-200 text-[#16344c] hover:border-[#268599]/40 hover:bg-gray-50"
                    )}
                    onClick={() => {
                      try {
                        setValue("metadata.typography.font.family", fontName);
                        setValue("metadata.typography.font.subset", "latin");
                        setValue("metadata.typography.font.variants", ["regular"]);
                      } catch {
                        // Silent fail
                      }
                    }}
                  >
                    <span className="truncate px-2 flex-1 text-center" style={{ fontFamily: fontName }}>{fontName}</span>
                    {isSelected && (
                      <div className="absolute top-1/2 -translate-y-1/2 right-2">
                        <Check size={14} weight="bold" className="text-[#268599]" />
                      </div>
                    )}
                  </button>
                </div>
              );
            })}
        </div>

        <div className="h-px bg-gray-100 w-full"></div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-500">
              <TextAa size={18} weight="duotone" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t`Header Font Family`}</span>
            </div>
            <span className="text-[10px] text-[#268599] font-medium bg-[#268599]/10 px-2 py-0.5 rounded-full truncate max-w-[120px]">
              {font.family}
            </span>
          </div>

          <div className="[&_[role=combobox]]:bg-white [&_[role=combobox]]:border-gray-200 [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:py-6 [&_[role=combobox]]:text-[#16344c]">
            <Combobox
              options={families.sort((a, b) => a.label.localeCompare(b.label))}
              value={font.family}
              searchPlaceholder={t`Search for a font family`}
              onValueChange={(value) => {
                try {
                  setValue("metadata.typography.font.family", value);
                  setValue("metadata.typography.font.subset", "latin");
                  setValue("metadata.typography.font.variants", ["regular"]);
                } catch {
                  // Silent fail
                }
              }}
            />
          </div>
        </div>

        <div className="h-px bg-gray-100 w-full"></div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-500">
              <TextAa size={18} weight="duotone" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t`Body Font Family`}</span>
            </div>
            <span className="text-[10px] text-[#268599] font-medium bg-[#268599]/10 px-2 py-0.5 rounded-full truncate max-w-[120px]">
              {metadata.typography.bodyFont?.family || font.family}
            </span>
          </div>

          <div className="[&_[role=combobox]]:bg-white [&_[role=combobox]]:border-gray-200 [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:py-6 [&_[role=combobox]]:text-[#16344c]">
            <Combobox
              options={families.sort((a, b) => a.label.localeCompare(b.label))}
              value={metadata.typography.bodyFont?.family || font.family}
              searchPlaceholder={t`Search for a body font`}
              onValueChange={(value) => {
                try {
                  setValue("metadata.typography.bodyFont", { family: value, subset: "latin", variants: ["regular"] });
                } catch {
                  // Silent fail
                }
              }}
            />
          </div>
        </div>


        {/* Subsets & Variants - Standard Combobox for now as they are secondary */}
        <div className="grid grid-cols-2 gap-x-4">
          <div className="space-y-1.5 [&_[role=combobox]]:bg-white [&_[role=combobox]]:border-gray-200 [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:py-5 [&_[role=combobox]]:text-[#16344c]">
            <Label className="text-[10px] text-gray-400 uppercase">{t`Subset`}</Label>
            <Combobox
              options={subsets}
              value={font.subset}
              searchPlaceholder={t`Search...`}
              onValueChange={(value) => {
                try {
                  setValue("metadata.typography.font.subset", value);
                } catch {}
              }}
            />
          </div>
          <div className="space-y-1.5 [&_[role=combobox]]:bg-white [&_[role=combobox]]:border-gray-200 [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:py-5 [&_[role=combobox]]:text-[#16344c]">
            <Label className="text-[10px] text-gray-400 uppercase">{t`Variants`}</Label>
            <Combobox
              multiple
              options={variants}
              value={font.variants || ["regular"]}
              searchPlaceholder={t`Search...`}
              onValueChange={(value) => {
                try {
                  setValue("metadata.typography.font.variants", value);
                } catch {}
              }}
            />
          </div>
        </div>
      </div>

      {/* Size & Spacing */}
      <div className="space-y-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="space-y-6">
          {/* Font Size Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-gray-500">
              <div className="flex items-center gap-2">
                <TextAUnderline size={18} />
                <span className="text-xs font-semibold uppercase tracking-wider">{t`Font Size`}</span>
              </div>
              <span className="text-xs font-mono font-bold text-[#16344c] bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                {font.size || 12}px
              </span>
            </div>

            <div className="pt-2 px-1 relative group flex items-center">
              <Slider
                min={10}
                max={24}
                step={0.5}
                value={[font.size || 12]}
                onValueChange={(value) => {
                  try {
                    setValue("metadata.typography.font.size", value[0]);
                  } catch {}
                }}
                className="[&>[role=slider]]:bg-[#268599] [&>[role=slider]]:border-none [&>[role=slider]]:shadow-[0_0_10px_rgba(38,133,153,0.5)] [&>.relative>.absolute]:bg-[#268599]/50"
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 font-mono px-2">
              <span>10</span>
              <span className="text-gray-400 text-xs">A</span>
              <span>24</span>
            </div>
          </div>

          <div className="h-px bg-gray-100 w-full"></div>

          {/* Header Font Size Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-gray-500">
              <div className="flex items-center gap-2">
                <TextAa size={18} />
                <span className="text-xs font-semibold uppercase tracking-wider">{t`Header Size`}</span>
              </div>
              <span className="text-xs font-mono font-bold text-[#16344c] bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                {metadata.typography.headerFontSize || 22}px
              </span>
            </div>

            <div className="pt-2 px-1 relative group flex items-center">
              <Slider
                min={14}
                max={40}
                step={0.5}
                value={[metadata.typography.headerFontSize || 22]}
                onValueChange={(value) => {
                  try {
                    setValue("metadata.typography.headerFontSize", value[0]);
                  } catch {}
                }}
                className="[&>[role=slider]]:bg-[#268599] [&>[role=slider]]:border-none [&>[role=slider]]:shadow-[0_0_10px_rgba(38,133,153,0.5)] [&>.relative>.absolute]:bg-[#268599]/50"
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 font-mono px-2">
              <span>14</span>
              <span className="text-gray-400 text-xs font-bold">H1</span>
              <span>40</span>
            </div>
          </div>

          <div className="h-px bg-gray-100 w-full"></div>

          {/* Header Text Transform */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-gray-500">
              <div className="flex items-center gap-2">
                <TextT size={18} />
                <span className="text-xs font-semibold uppercase tracking-wider">{t`Header Casing`}</span>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {[
                { value: "uppercase", label: "UPPER", className: "uppercase" },
                { value: "capitalize", label: "Capital", className: "capitalize" },
                { value: "lowercase", label: "lower", className: "lowercase" },
                { value: "none", label: "Normal", className: "normal-case" }
              ].map((option) => {
                // Handle fallback default to 'uppercase'
                const currentTransform = metadata.typography.headerTransform || "uppercase";
                return (
                  <button
                    key={option.value}
                    className={cn(
                      "h-8 rounded-lg text-[10px] font-medium transition-all",
                      option.className,
                      currentTransform === option.value
                        ? "bg-[#268599]/10 text-[#268599] border border-[#268599]/30"
                        : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-100 hover:text-[#16344c]"
                    )}
                    onClick={() => {
                      try {
                        setValue("metadata.typography.headerTransform", option.value);
                      } catch {}
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-gray-100 w-full"></div>

          {/* Line Height Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-gray-500">
              <div className="flex items-center gap-2">
                <ArrowsOutLineVertical size={18} />
                <span className="text-xs font-semibold uppercase tracking-wider">{t`Line Height`}</span>
              </div>
              <span className="text-xs font-mono font-bold text-[#16344c] bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                {metadata.typography.lineHeight || 1.5}
              </span>
            </div>

            <div className="pt-2 px-1 relative flex items-center">
              <Slider
                min={0.8}
                max={2.5}
                step={0.05}
                value={[metadata.typography.lineHeight || 1.5]}
                onValueChange={(value) => {
                  try {
                    setValue("metadata.typography.lineHeight", value[0]);
                  } catch {}
                }}
                className="[&>[role=slider]]:bg-[#268599] [&>[role=slider]]:border-none [&>[role=slider]]:shadow-[0_0_10px_rgba(38,133,153,0.5)] [&>.relative>.absolute]:bg-[#268599]/50"
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 font-mono px-2 items-center">
              <span>0.8</span>
              <ArrowsOutLineVertical size={12} className="text-[#268599]/50" />
              <span>2.5</span>
            </div>
          </div>
        </div>
      </div>

      {/* Typography Toggles */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-500">
            <ToggleLeft size={18} weight="duotone" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t`Advanced Options`}</span>
          </div>
        </div>

        <div className="space-y-2">
          {/* Hide Icons Toggle */}
          <label className="flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:border-[#268599]/30 hover:bg-gray-50 transition-all cursor-pointer group">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400 group-hover:text-[#16344c] transition-colors">
                <EyeSlash size={16} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-[#16344c]">{t`Hide Resume Icons`}</span>
                <span className="text-[10px] text-gray-400">{t`Remove icons from section headers`}</span>
              </div>
            </div>

            {/* Light toggle switch */}
            <div className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={metadata.typography.hideIcons || false}
                onChange={(e) => {
                  try {
                    setValue("metadata.typography.hideIcons", e.target.checked);
                  } catch {}
                }}
              />
              <div className="h-full w-full rounded-full bg-gray-200 border border-gray-300 transition-all peer-checked:bg-[#268599] peer-checked:border-[#268599]"></div>
              <div className="pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all peer-checked:translate-x-4"></div>
            </div>
          </label>

          {/* Underline Links Toggle */}
          <label className="flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:border-[#268599]/30 hover:bg-gray-50 transition-all cursor-pointer group">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400 group-hover:text-[#16344c] transition-colors">
                <LinkIcon size={16} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-[#16344c]">{t`Underline Links`}</span>
                <span className="text-[10px] text-gray-400">{t`Apply underline to all URLs`}</span>
              </div>
            </div>

            <div className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={metadata.typography.underlineLinks || false}
                onChange={(e) => {
                  try {
                    setValue("metadata.typography.underlineLinks", e.target.checked);
                  } catch {}
                }}
              />
              <div className="h-full w-full rounded-full bg-gray-200 border border-gray-300 transition-all peer-checked:bg-[#268599] peer-checked:border-[#268599]"></div>
              <div className="pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all peer-checked:translate-x-4"></div>
            </div>
          </label>
        </div>
      </div>

      {/* Live Preview Mini Box */}
      <div className="mt-4 p-4 rounded-xl border border-[#268599]/20 bg-[#268599]/5 relative overflow-hidden hidden">
        <div className="flex items-center gap-2 mb-2 text-[#268599]">
          <MagicWand size={18} />
          <span className="text-[10px] font-bold uppercase tracking-wider">{t`Live Preview`}</span>
        </div>
        <div
          className="text-[#16344c] transition-all"
          style={{
            fontFamily: font.family,
            fontSize: `${font.size || 12}px`,
            lineHeight: metadata.typography.lineHeight || 1.5
          }}
        >
          {t`The quick brown fox jumps over the lazy dog.`}
          <span className={metadata.typography.underlineLinks ? "underline text-[#268599] ml-1 cursor-pointer" : "text-[#268599] ml-1 cursor-pointer"}>
            https://stylion.io
          </span>
        </div>
      </div>

      </div>
    </div>
  );
};
